import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { whatsAppNotificationSchema } from "../shemas/broadcast";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  BroadcastAppCard,
  ContainerActions,
  FormContainer,
  FormInputContainer,
  FormInputLabel,
  FormInputDescription,
  FormInputError,
  PhoneInputField,
  AddPhoneButton,
  PhoneChipsContainer,
  PhoneChipRemove,
  NotificationPanel,
  NotificationTitle,
  NotificationIcon,
  NotificationText,
  NotificationContainer,
  CountryPhoneRow,
  CountrySelectorTrigger,
  CountrySelectorDropdown,
  CountrySelectorOption,
  CountrySelectorSearch,
  PhoneChipEnhanced,
  PhoneChipFlag,
  PhoneChipCode,
  PhoneChipNumber,
  PhoneCountBadge,
  EmptyPhoneStateEnhanced,
  SectionHeaderIcon,
} from "../styles";
import { BackLink } from "../../Campaigns.style";
import Button from "../../../../../components/shared/components/button/Button.style";
import { COUNTRIES_LIST } from "../constants/countries";
import { getFlagEmoji } from "../helpers/emoji";

const DEFAULT_NAMEODE = "MX";

const getCountryByNameCode = (nameCode) =>
  COUNTRIES_LIST.find((c) => c.nameCode === nameCode) || null;

export const WhatsAppNotificationForm = ({
  handleNext,
  handleBack,
  updateBroadcastData,
  broadcastData,
}) => {
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneInputError, setPhoneInputError] = useState("");
  const [selectedNameCode, setSelectedNameCode] = useState(DEFAULT_NAMEODE);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Internal phone entries: { phone: string, nameCode: string }[]
  const [phoneEntries, setPhoneEntries] = useState(() => {
    const existing = broadcastData?.phonesToNotify;
    if (!existing || existing.length === 0) return [];
    // Handle backward compat: existing may be plain strings
    return existing.map((p) =>
      typeof p === "string" ? { phone: p, nameCode: DEFAULT_NAMEODE } : p,
    );
  });

  const { register, handleSubmit, errors, setValue, watch } = useForm({
    validationSchema: whatsAppNotificationSchema,
    defaultValues: {
      phonesToNotify: broadcastData?.phonesToNotify || [],
    },
    mode: "onChange",
  });

  const phonesToNotify = watch("phonesToNotify");

  // Keep react-hook-form in sync with internal state
  useEffect(() => {
    const phones = phoneEntries.map((e) => e.phone);
    setValue("phonesToNotify", phones, { shouldValidate: true });
  }, [phoneEntries, setValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCountry = getCountryByNameCode(selectedNameCode);

  const filteredCountries = countrySearch
    ? COUNTRIES_LIST.filter(
        (c) =>
          c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.includes(countrySearch) ||
          c.nameCode.toLowerCase().includes(countrySearch.toLowerCase()),
      )
    : COUNTRIES_LIST;

  const validateLocalNumber = (local) => {
    const digits = local.replace(/\D/g, "");
    if (!local.trim()) {
      return "El número no puede estar vacío";
    }
    if (digits.length < 5 || digits.length > 15) {
      return "El número debe tener entre 5 y 15 dígitos";
    }
    return null;
  };

  const handleAddPhone = () => {
    const trimmedLocal = phoneInput.trim();
    const error = validateLocalNumber(trimmedLocal);
    if (error) {
      setPhoneInputError(error);
      return;
    }

    const country = getCountryByNameCode(selectedNameCode);
    const fullPhone = `+${country.code}${trimmedLocal}`;

    // Check duplicates against full number
    if (phoneEntries.some((e) => e.phone === fullPhone)) {
      setPhoneInputError("Este teléfono ya está agregado");
      return;
    }

    if (phoneEntries.length >= 8) {
      setPhoneInputError("Solo se pueden agregar hasta 8 teléfonos");
      return;
    }

    const newEntry = { phone: fullPhone, nameCode: selectedNameCode };
    setPhoneEntries((prev) => [...prev, newEntry]);
    setPhoneInput("");
    setPhoneInputError("");
  };

  const handleRemovePhone = (fullPhone) => {
    setPhoneEntries((prev) => prev.filter((e) => e.phone !== fullPhone));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddPhone();
    }
  };

  const handleCountrySelect = (nameCode) => {
    setSelectedNameCode(nameCode);
    setDropdownOpen(false);
    setCountrySearch("");
  };

  const onSubmit = (data) => {
    // Backward compat: send flat string[] of phone numbers only
    updateBroadcastData({
      phonesToNotify: phoneEntries.map((e) => e.phone),
    });
    handleNext();
  };

  const handleOnBack = () => {
    updateBroadcastData({
      phonesToNotify: [],
    });
    handleBack();
  };

  useEffect(() => {
    register("phonesToNotify");
  }, [register]);

  const triggerLabel = selectedCountry
    ? `${getFlagEmoji(selectedCountry.nameCode)} +${selectedCountry.code}`
    : "🌐";

  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <NotificationContainer>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <FormInputContainer>
              <FormInputLabel htmlFor="phoneInput">
                <SectionHeaderIcon>📱</SectionHeaderIcon>
                Teléfonos para notificación
              </FormInputLabel>

              <FormInputDescription>
                Agrega hasta 8 números. Selecciona el código de país y escribe
                el número local.
              </FormInputDescription>

              {/* Country selector + phone input row */}
              <div style={{ position: "relative" }}>
                <CountryPhoneRow>
                  <CountrySelectorTrigger
                    ref={triggerRef}
                    type="button"
                    onClick={() => {
                      setDropdownOpen((prev) => !prev);
                      setCountrySearch("");
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={dropdownOpen}
                  >
                    {triggerLabel}
                    <span
                      style={{
                        fontSize: "0.65rem",
                        color: "#9ca3af",
                        marginLeft: 2,
                      }}
                    >
                      ▼
                    </span>
                  </CountrySelectorTrigger>

                  <PhoneInputField
                    type="text"
                    id="phoneInput"
                    value={phoneInput}
                    onChange={(e) => {
                      setPhoneInput(e.target.value);
                      setPhoneInputError("");
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="1234567890"
                    error={phoneInputError}
                    style={{
                      borderRadius: "0",
                      borderLeft: "none",
                      flex: 1,
                    }}
                  />

                  <AddPhoneButton
                    type="button"
                    onClick={handleAddPhone}
                    disabled={!phoneInput.trim() || phoneEntries.length >= 8}
                    style={{ borderRadius: "0 8px 8px 0", flexShrink: 0 }}
                  >
                    Agregar
                  </AddPhoneButton>
                </CountryPhoneRow>

                {dropdownOpen && (
                  <CountrySelectorDropdown ref={dropdownRef} role="listbox">
                    <CountrySelectorSearch
                      type="text"
                      placeholder="Buscar país..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                    {filteredCountries.length === 0 ? (
                      <div
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "#9ca3af",
                          fontSize: "0.875rem",
                          fontFamily: '"Inter", sans-serif',
                        }}
                      >
                        Sin resultados
                      </div>
                    ) : (
                      filteredCountries.map((c) => (
                        <CountrySelectorOption
                          key={c.nameCode}
                          $selected={c.nameCode === selectedNameCode}
                          onClick={() => handleCountrySelect(c.nameCode)}
                          role="option"
                          aria-selected={c.nameCode === selectedNameCode}
                        >
                          <span style={{ fontSize: "1.1rem" }}>
                            {getFlagEmoji(c.nameCode)}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              fontSize: "0.875rem",
                              color: "#374151",
                            }}
                          >
                            {c.country}
                          </span>
                          <span
                            style={{ fontSize: "0.8rem", color: "#9ca3af" }}
                          >
                            +{c.code}
                          </span>
                        </CountrySelectorOption>
                      ))
                    )}
                  </CountrySelectorDropdown>
                )}
              </div>

              {phoneInputError && (
                <FormInputError>{phoneInputError}</FormInputError>
              )}

              {errors.phonesToNotify && (
                <FormInputError>{errors.phonesToNotify.message}</FormInputError>
              )}
            </FormInputContainer>

            <FormInputContainer>
              <FormInputLabel
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                Teléfonos agregados
                <PhoneCountBadge $count={phoneEntries.length}>
                  {phoneEntries.length}/8
                </PhoneCountBadge>
              </FormInputLabel>

              <PhoneChipsContainer>
                {phoneEntries.length === 0 ? (
                  <EmptyPhoneStateEnhanced>
                    <span>No hay teléfonos agregados aún</span>
                  </EmptyPhoneStateEnhanced>
                ) : (
                  phoneEntries.map((entry) => {
                    const country = getCountryByNameCode(entry.nameCode);
                    const flag = country ? getFlagEmoji(country.nameCode) : "";
                    const code = country ? `+${country.code}` : "";
                    // Extract local part after the country code prefix
                    const localPart = code
                      ? entry.phone.replace(`+${country.code}`, "")
                      : entry.phone;

                    return (
                      <PhoneChipEnhanced key={entry.phone}>
                        <PhoneChipFlag>{flag}</PhoneChipFlag>
                        <PhoneChipCode>{code}</PhoneChipCode>
                        <PhoneChipNumber>{localPart}</PhoneChipNumber>
                        <PhoneChipRemove
                          type="button"
                          onClick={() => handleRemovePhone(entry.phone)}
                          aria-label={`Eliminar ${entry.phone}`}
                          style={{ color: "#9ca3af", marginLeft: 4 }}
                        >
                          ×
                        </PhoneChipRemove>
                      </PhoneChipEnhanced>
                    );
                  })
                )}
              </PhoneChipsContainer>
            </FormInputContainer>
          </FormContainer>

          <NotificationPanel>
            <NotificationTitle>
              <NotificationIcon />
              ¿Por qué notificar?
            </NotificationTitle>

            <NotificationText>
              Los teléfonos agregados recibirán una notificación de WhatsApp
              cuando la campaña sea enviada exitosamente. Esto te permite:
            </NotificationText>

            <ul
              style={{
                margin: "1rem 0 0 0",
                paddingLeft: "1.5rem",
                fontSize: "0.875rem",
                color: "#0c4a6e",
                lineHeight: "1.8",
              }}
            >
              <li>Confirmar que la campaña se ejecutó correctamente</li>
              <li>Coordinar acciones de seguimiento con tu equipo</li>
              <li>Mantener un registro de envíos importantes</li>
            </ul>

            <NotificationText style={{ marginTop: "1rem" }}>
              <strong>Nota:</strong> Las notificaciones son opcionales. Puedes
              continuar sin agregar teléfonos.
            </NotificationText>
          </NotificationPanel>
        </NotificationContainer>

        <ContainerActions>
          <BackLink onClick={handleOnBack}>← Atrás</BackLink>

          <Button designV4 onClick={handleSubmit(onSubmit)}>
            Continuar
          </Button>
        </ContainerActions>
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
